import { motion } from 'framer-motion';
import { ReactNode } from 'react';

interface PageBackgroundProps {
  children: ReactNode;
  variant?: 'default' | 'grid' | 'gradient' | 'blueprint';
}

export function PageBackground({ children, variant = 'default' }: PageBackgroundProps) {
  return (
    // No will-change-transform on this outer wrapper: nothing on it animates a
    // transform, and it's the direct ancestor of every `position: fixed`
    // element on the page (ScrollProgress, FloatingContactWidget, ScrollToTop,
    // all rendered as {children} via Layout.tsx) — that hint alone creates a
    // new containing block for them, so they stopped tracking the viewport
    // and instead pinned to this div's (page-height-tall) box. The inner
    // "Background layers" div below keeps its own will-change-transform: it's
    // itself position:fixed (not a container the widgets live inside), and it
    // genuinely holds animated-transform children (the gradient orbs).
    <div className="relative min-h-screen overflow-hidden">
      {/* Background layers */}
      <div className="fixed inset-0 -z-10 overflow-hidden will-change-transform">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-linear-to-br from-background via-background to-muted/30" />
        
        {/* Animated gradient orbs */}
        <motion.div
          className="absolute top-0 -left-1/4 w-1/2 h-1/2 rounded-full opacity-30 blur-3xl will-change-transform"
          style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.3) 0%, transparent 70%)' }}
          animate={{
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-0 -right-1/4 w-1/2 h-1/2 rounded-full opacity-20 blur-3xl will-change-transform"
          style={{ background: 'radial-gradient(circle, hsl(var(--accent) / 0.3) 0%, transparent 70%)' }}
          animate={{
            x: [0, -50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        
        {/* Grid pattern overlay */}
        {(variant === 'grid' || variant === 'blueprint') && (
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `
                linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
                linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
            }}
          />
        )}
        
        {/* Blueprint variant - extra grid lines */}
        {variant === 'blueprint' && (
          <div 
            className="absolute inset-0 opacity-[0.02]"
            style={{
              backgroundImage: `
                linear-gradient(hsl(var(--primary)) 1px, transparent 1px),
                linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)
              `,
              backgroundSize: '200px 200px',
            }}
          />
        )}
        
        {/* Noise texture */}
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />
      </div>
      
      {children}
    </div>
  );
}
